import * as React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { ModalFormHeader, MODAL_BODY_CLS, MODAL_FOOTER_CLS } from "@/components/ui/modal-form";
import { VisorArchivo } from "@/components/ui/zoom-archivo";
// Misma resolucion que ModalViewer (un solo hook): el arreglo de URLs de otro proyecto vale para los dos.
import { useUrlRecurso } from "@/hooks/useUrlRecurso";

/**
 * ModalViewerDetail - modal partido: un recurso visual (PDF, imagen, doc) a un
 * lado e información/datos escritos al otro (header + body + footer).
 *
 * Por defecto el recurso va a la IZQUIERDA. Con `resourceSide="right"` se
 * invierte: primero el formulario y la vista previa a la derecha (útil cuando el
 * usuario elige/sube el archivo dentro del propio modal y la previsualización es
 * consecuencia de lo que capturó).
 *
 * Caso de uso: ver una evidencia junto a su detalle/edición (p. ej. comprobante
 * de pago + formulario de validación). Agnóstico al tipo de archivo. Resuelve
 * rutas del bucket privado `firmas-digitales` y documentos Mifiel; URLs públicas
 * directo. Componente padre reutilizable (piloto: portal de agentes).
 *
 * Para mostrar SOLO el recurso visual, ver `ModalViewer`.
 */

export interface ModalViewerDetailProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Recurso visual a mostrar a la izquierda (PDF / imagen / doc). */
  resourceUrl?: string | null;
  /**
   * Panel izquierdo propio (carrusel, mapa, plano interactivo…). Si se pasa,
   * manda sobre `resourceUrl` y el componente no resuelve ninguna URL.
   */
  resource?: React.ReactNode;
  /** Contenido/detalle escrito (panel derecho). */
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** ancho/estilos del contenedor (default max-w-4xl). */
  className?: string;
  bodyClassName?: string;
  /** Estilos extra del panel del recurso (p. ej. `aspect-[4/3] md:aspect-auto`). */
  resourceClassName?: string;
  /**
   * Reparto de columnas en desktop. Por defecto mitad y mitad; para un PDF de
   * contrato conviene darle mas aire al recurso (`md:grid-cols-[1.15fr_1fr]`)
   * en vez de estirar el modal a lo ancho de la pantalla.
   */
  gridClassName?: string;
  /** Lado del recurso visual en desktop. Default `left`. */
  resourceSide?: "left" | "right";
}

export function ModalViewerDetail({
  open,
  onOpenChange,
  title,
  subtitle,
  resourceUrl,
  resource: customResource,
  children,
  footer,
  className,
  bodyClassName,
  resourceClassName,
  gridClassName,
  resourceSide = "left",
}: ModalViewerDetailProps) {
  const { url, loading, error } = useUrlRecurso(open, resourceUrl);

  // En móvil el recurso siempre va arriba; en desktop el orden lo decide resourceSide.
  const resourceWrapperCls = cn(
    "relative min-h-[240px] overflow-hidden bg-muted md:h-[90vh] md:min-h-0",
    resourceSide === "right" && "md:order-2",
    resourceClassName,
  );

  const resource = customResource ? (
    <div className={resourceWrapperCls}>{customResource}</div>
  ) : (
    <div className={cn(resourceWrapperCls, "flex flex-col")}>
      {loading ? (
        <div className="flex h-full items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error || !url ? (
        <div className="flex h-full flex-col items-center justify-center gap-2 p-6 py-16 text-center">
          <AlertCircle className="h-9 w-9 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">{error || "Sin recurso para mostrar."}</p>
        </div>
      ) : (
        // Imagen → zoom solo sobre la foto; PDF → visor nativo. El tipo sale de la extensión del
        // recurso original (la URL firmada puede no traerla) o del servidor.
        <VisorArchivo
          src={url}
          nombre={resourceUrl}
          title={typeof title === "string" ? title : "Recurso"}
          className="flex-1 min-h-[240px] md:min-h-0"
          loading="lazy"
        />
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("w-full gap-0 overflow-hidden p-0 sm:w-[95vw] sm:max-w-4xl", className)}>
        <div className={cn("grid max-h-[90vh] grid-cols-1 md:grid-cols-2", gridClassName)}>
          {/* Recurso visual (izquierda por defecto, derecha con resourceSide) */}
          {resource}
          {/* Header + detalle + footer */}
          <div className={cn("flex min-h-0 flex-col md:max-h-[90vh]", resourceSide === "right" && "md:order-1")}>
            <ModalFormHeader title={title} subtitle={subtitle} />
            <div className={cn(MODAL_BODY_CLS, "flex-1", bodyClassName)}>{children}</div>
            {footer ? <div className={MODAL_FOOTER_CLS}>{footer}</div> : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
